import { isFinite } from '@bluebrick/saas-ui'
import { type Duration, Interval } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { formatDuration, getDifferenceInDays, getDuration } from '@/common/utils'
import { type EventDurationsMap } from '@/features/eventDetails/types'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventDetailsSourceEvent } from '@/features/eventDetails/useEventDetailsSourceEvent'
import { useEventDetailsStoreData } from '@/features/eventDetails/useEventDetailsStoreData'
import { type DiscountConfig } from '@/types'

export const useEventDetailsDurationStore = defineStore('eventDetailsDurationStore', () => {
	const { startDate, endDate, sourceEvent, storeId } = storeToRefs(useEventDetailsBaseStore())
	const { isFromMajorStoreOpportunity, isFromOpportunity } = useEventDetailsSourceEvent()
	const { canCalculateEventParameters, selectedSkuIds } = useEventDetailsStoreData()

	// An optimal duration that we recommend for current event
	const optimalDurationInDays = ref<number>()
	const optimalEventDurationFormatted = computed(() => formatDuration(optimalDurationInDays.value))
	// An maximal duration that is possible for current event according to store rules and timeline context
	const maxDurationInDays = ref<number>()
	const maxEventDurationFormatted = computed(() => formatDuration(maxDurationInDays.value))

	const isOptimalEventDurationValid = computed(() => isFinite(optimalDurationInDays.value))

	const eventDurationInDays = computed(() => (startDate.value && endDate.value ? getDifferenceInDays(endDate.value, startDate.value) : 0))

	const eventDuration = computed<Duration | null>(() => {
		if (!startDate.value.isValid || !endDate.value.isValid) {
			return null
		}

		return getDuration(endDate.value, startDate.value)
	})

	const eventIntervalUTC = computed(() =>
		startDate.value.isValid && endDate.value.isValid ? Interval.fromDateTimes(startDate.value.toUTC(), endDate.value.toUTC()) : null
	)

	const isEventTooLong = computed(() => {
		return (
			canCalculateEventParameters.value &&
			!isFromMajorStoreOpportunity.value &&
			!!eventDuration.value &&
			isFinite(maxDurationInDays.value) &&
			eventDurationInDays.value > maxDurationInDays.value
		)
	})

	const isEventLongerThanOptimal = computed(() => {
		return (
			canCalculateEventParameters.value &&
			!isFromMajorStoreOpportunity.value &&
			!!eventDuration.value &&
			isFinite(optimalDurationInDays.value) &&
			eventDurationInDays.value > optimalDurationInDays.value
		)
	})

	const isRecommendedEventDuration = computed(() => eventDurationInDays.value === optimalDurationInDays.value)

	/**
	 * Takes maximal and optimal event durations per SKU fetched from API for currently selected SKUs
	 * and event start date.
	 * Calculates local maximal and optimal event duration by taking the shortest value among available
	 * durations.
	 * If there are no event durations per SKU available then uses default maximal and optimal event
	 * durations from discount config for current store.
	 */
	function updateRecommendedAndOptimalDurations(discountConfig: DiscountConfig, eventDurations: EventDurationsMap) {
		if (!storeId.value) {
			reset()

			return
		}

		let recommendedOptimalDurationInDays: number | undefined
		let recommendedMaxDurationInDays: number | undefined

		if (
			canCalculateEventParameters.value &&
			isFromOpportunity.value &&
			sourceEvent.value &&
			sourceEvent.value.start_date?.isValid &&
			sourceEvent.value.end_date?.isValid
		) {
			// For opportunities the recommended durations will be simply current length of the source opportunity event.
			recommendedOptimalDurationInDays = getDifferenceInDays(sourceEvent.value.start_date, sourceEvent.value.end_date)
			recommendedMaxDurationInDays = getDifferenceInDays(sourceEvent.value.start_date, sourceEvent.value.end_date)
		} else {
			// In all other cases we will use default values from discount config
			recommendedOptimalDurationInDays = discountConfig.optimalLengthInDays
			recommendedMaxDurationInDays = discountConfig.maxLengthInDays
		}

		if (selectedSkuIds.value.length === 0 || eventDurations.size === 0) {
			optimalDurationInDays.value = recommendedOptimalDurationInDays
			maxDurationInDays.value = recommendedMaxDurationInDays

			return
		}

		let minDuration = Infinity
		let optimalDuration = 0
		let maxDuration = 0

		selectedSkuIds.value.forEach((skuId) => {
			const optimalDurationForProduct = eventDurations.get(skuId)?.optimalLength
			const maxDurationForProduct = eventDurations.get(skuId)?.maxLength

			if (isFinite(optimalDurationForProduct)) {
				if (optimalDurationForProduct > optimalDuration) {
					optimalDuration = optimalDurationForProduct
				}

				if (optimalDurationForProduct < minDuration) {
					minDuration = optimalDurationForProduct
				}
			}

			if (isFinite(maxDurationForProduct)) {
				if (maxDurationForProduct > maxDuration) {
					maxDuration = maxDurationForProduct
				}

				if (maxDurationForProduct < minDuration) {
					minDuration = maxDurationForProduct
				}
			}
		})

		if (isFinite(minDuration) && minDuration > 0) {
			optimalDurationInDays.value = minDuration
		} else if (isFinite(optimalDuration) && optimalDuration > 0) {
			optimalDurationInDays.value = optimalDuration
		} else {
			optimalDurationInDays.value = recommendedOptimalDurationInDays
		}

		if (isFinite(maxDuration) && maxDuration > 0) {
			maxDurationInDays.value = maxDuration
		} else {
			maxDurationInDays.value = recommendedMaxDurationInDays
		}
	}

	function reset() {
		optimalDurationInDays.value = undefined
		maxDurationInDays.value = undefined
	}

	return {
		updateRecommendedAndOptimalDurations,
		reset,
		optimalDurationInDays,
		optimalEventDurationFormatted,
		maxDurationInDays,
		maxEventDurationFormatted,
		eventDuration,
		eventIntervalUTC,
		isOptimalEventDurationValid,
		isRecommendedEventDuration,
		isEventTooLong,
		isEventLongerThanOptimal
	}
})
